import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { Divider, FormControlLabel, Radio, RadioGroup, Stack, Typography } from '@mui/material';
import { CalendarEventShowAs, CalendarPreference } from '@demind-inc/core';
import { debounce } from 'lodash';
import { useTranslation } from 'react-i18next';

import {
  useAuthContext,
  useCalendarContext,
  useUpdateCalendarPref,
} from '../../../../../../data-access';
import { ReactComponent as EventAvailIcon } from '../../../../../../assets/tabs/settings-tab/event-avail-icon.svg';
import './EventAvailability.scss';

const EVENT_AVAILABILITY_OPTIONS: { value: CalendarEventShowAs; labelKey: string }[] = [
  { value: 'available', labelKey: 'settings.calendar.available' },
  { value: 'busy', labelKey: 'settings.calendar.busy' },
];

const EventAvailability: FC = () => {
  const { t } = useTranslation();
  const { user } = useAuthContext();
  const { updateCalendarPref } = useUpdateCalendarPref();
  const { calendarPreferences } = useCalendarContext();

  const [aiEventTransparency, setAiEventTransparency] = useState<CalendarEventShowAs>('available');

  useEffect(() => {
    setAiEventTransparency(calendarPreferences.transparency?.aiScheduler || 'available');
  }, [calendarPreferences.transparency?.aiScheduler]);

  const debouncedUpdateCalendarSettings = useMemo(
    () =>
      debounce((newData: Partial<CalendarPreference>) => {
        if (!user.preferenceId) {
          return;
        }

        return updateCalendarPref({
          preferenceId: user.preferenceId,
          preferences: { ...newData },
        });
      }, 1000),
    [updateCalendarPref, user.preferenceId]
  );

  useEffect(() => {
    return () => {
      debouncedUpdateCalendarSettings.cancel();
    };
  }, [debouncedUpdateCalendarSettings]);

  const handleAiEventTransparencyChange = useCallback(
    (newValue: CalendarEventShowAs) => {
      setAiEventTransparency(newValue);

      debouncedUpdateCalendarSettings({
        transparency: {
          ...calendarPreferences.transparency,
          aiScheduler: newValue,
        },
      });
    },
    [calendarPreferences.transparency, debouncedUpdateCalendarSettings]
  );

  return (
    <Stack
      id="eventAvailability"
      gap={2}
      className="event-availability"
      sx={{ scrollMarginTop: '12px' }}
    >
      <Stack gap={1} direction="row" alignItems="center">
        <EventAvailIcon />
        <Typography variant="body1" className="event-availability__section-title">
          {t('settings.calendar.eventAvailability')}
        </Typography>
      </Stack>

      <Typography variant="subtitle2">
        {t('settings.calendar.eventAvailabilityDescription')}
      </Typography>

      <Typography variant="subtitle2" className="event-availability__container-title">
        {t('settings.calendar.aiGeneratedEvents')}
      </Typography>

      <RadioGroup
        value={aiEventTransparency}
        onChange={(event) =>
          handleAiEventTransparencyChange(event.target.value as CalendarEventShowAs)
        }
        className="event-availability__radio-group"
      >
        {EVENT_AVAILABILITY_OPTIONS.map((option, index) => (
          <Stack key={option.value}>
            <FormControlLabel
              key={option.value}
              value={option.value}
              control={<Radio size="small" className="event-availability__radio-item__control" />}
              label={
                <Typography variant="body1" className="event-availability__radio-item__label">
                  {t(option.labelKey)}
                </Typography>
              }
              className="event-availability__radio-item"
            />
            {index !== EVENT_AVAILABILITY_OPTIONS.length - 1 && (
              <Divider className="event-availability__radio-item__divider" />
            )}
          </Stack>
        ))}
      </RadioGroup>
    </Stack>
  );
};

export default EventAvailability;
